import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface Customer {
  id?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  dateOfBirth?: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
  };
  preferences: {
    skinType: 'oily' | 'dry' | 'combination' | 'sensitive' | '';
    hairType: 'straight' | 'wavy' | 'curly' | 'coily' | '';
    allergies: string[];
    notes: string;
  };
}

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email'),
  phone: yup.string().required('Phone number is required'),
  dateOfBirth: yup.string(),
  address: yup.object({
    street: yup.string(),
    city: yup.string(),
    state: yup.string(),
    pincode: yup.string(),
  }),
  preferences: yup.object({
    skinType: yup.string(),
    hairType: yup.string(),
    allergies: yup.array().of(yup.string()),
    notes: yup.string(),
  }),
});

interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Customer) => void;
  customer?: Customer | null;
  loading?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  open,
  onClose,
  onSubmit,
  customer,
  loading = false,
}) => {
  const [allergies, setAllergies] = React.useState<string[]>(customer?.preferences.allergies || []);
  const [allergyInput, setAllergyInput] = React.useState('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Customer>({
    resolver: yupResolver(schema) as any,
    defaultValues: customer || {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      address: {
        street: '',
        city: '',
        state: '',
        pincode: '',
      },
      preferences: {
        skinType: '',
        hairType: '',
        allergies: [],
        notes: '',
      },
    },
  });

  React.useEffect(() => {
    if (customer) {
      reset(customer);
      setAllergies(customer.preferences.allergies || []);
    } else {
      reset({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        address: {
          street: '',
          city: '',
          state: '',
          pincode: '',
        },
        preferences: {
          skinType: '',
          hairType: '',
          allergies: [],
          notes: '',
        },
      });
      setAllergies([]);
    }
  }, [customer, reset]);

  const handleAddAllergy = () => {
    if (allergyInput.trim() && !allergies.includes(allergyInput.trim())) {
      setAllergies([...allergies, allergyInput.trim()]);
      setAllergyInput('');
    }
  };

  const handleRemoveAllergy = (allergyToRemove: string) => {
    setAllergies(allergies.filter(allergy => allergy !== allergyToRemove));
  };

  const handleFormSubmit = (data: Customer) => {
    onSubmit({
      ...data,
      preferences: {
        ...data.preferences,
        allergies,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {customer ? 'Edit Customer' : 'Add New Customer'}
      </DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="First Name"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Last Name"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone Number"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="address.street"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Street Address"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="address.city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="City"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="address.state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="State"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="address.pincode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Pincode"
                  />
                )}
              />
            </Grid>

            {/* Preferences */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Preferences
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="preferences.skinType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Skin Type</InputLabel>
                    <Select {...field} label="Skin Type">
                      <MenuItem value="oily">Oily</MenuItem>
                      <MenuItem value="dry">Dry</MenuItem>
                      <MenuItem value="combination">Combination</MenuItem>
                      <MenuItem value="sensitive">Sensitive</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="preferences.hairType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Hair Type</InputLabel>
                    <Select {...field} label="Hair Type">
                      <MenuItem value="straight">Straight</MenuItem>
                      <MenuItem value="wavy">Wavy</MenuItem>
                      <MenuItem value="curly">Curly</MenuItem>
                      <MenuItem value="coily">Coily</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Allergies */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  label="Add Allergy"
                  value={allergyInput}
                  onChange={(e) => setAllergyInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAllergy();
                    }
                  }}
                />
                <Button variant="outlined" onClick={handleAddAllergy}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {allergies.map((allergy) => (
                  <Chip
                    key={allergy}
                    label={allergy}
                    onDelete={() => handleRemoveAllergy(allergy)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="preferences.notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit(handleFormSubmit as any)}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Saving...' : customer ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerForm;